/* Canonical project roles — designs/canonical-roles.md. ONE org-wide list,
   never free text (settled decision 3 applied to roles) and, since CR2, never
   hardcoded either: the catalog is recorded, stamped store data. What lives
   here is the type, the pure rules over a list of roles, and the migration
   seed the store plants once (CR5). */

import type { Stamp, UserRef } from "./organizer-roles";
/* the app's one "a person entered this" source string — projects.ts imports
   only TYPES from here, so this direction adds no runtime cycle */
import { ENTERED_SRC } from "./projects";

export interface Role {
  id: string;
  name: string;
  /* the single-holder flag (CR3): "one" = a project may fill it with one
     person only (project lead, scrum master); "many" = several */
  cardinality: "one" | "many";
  /* one line, or null = NOT RECORDED (red, settled decision 2) — the
     migrated roles have none and none is invented for them */
  description: string | null;
  /* who recorded it and when (CR3); `by` is absent on the migration seed */
  recorded: Stamp;
  /* out of circulation (CR16): not offered for new role needs or
     allocations, still shown wherever a project already recorded it.
     Absent means in circulation — retiring is a flag, never a deletion. */
  retired?: boolean;
}

/* what an actor submits — the id and the stamp are the app's to mint */
export interface RoleInput {
  name: string;
  description: string | null;
  cardinality: Role["cardinality"];
}

/* The roles whose holders may change the catalog (CR8). Their ids are the
   migrated ones, unchanged since the constant era, so this predicate keeps
   naming the same two roles no matter what else the catalog grows. */
export const CATALOG_ROLE_IDS: readonly string[] = ["project-lead", "project-manager"];

/* one name, one identity: case and inner spacing are not a difference */
export const normalizeName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, " ");

/* the slug IS the id (CR4), so the duplicate rule and the storage key are
   the same fact and cannot drift */
export const slugId = (name: string): string => normalizeName(name).replace(/ /g, "-");

const acronymOf = (normalized: string): string =>
  normalized
    .split(" ")
    .map((word) => word[0])
    .join("");

/* Near-matches are PRESENTATION (CR4): shown as the actor types so they can
   see "Project Manager" before adding "PM", and never a reason to refuse a
   save — "Localization Lead" and "Localization SME" are both legitimate. */
/* one letter is not a hint, it is a dump of the catalog (review 14) */
const NEAR_MIN = 2;

/* Widened to any named item (pdp-catalog.md PC4): the PDP catalog's
   deliverables are searched by exactly this rule, and it only ever read
   `.name`. Every roles caller is unchanged. */
export function nearMatches<T extends { name: string }>(items: T[], query: string): T[] {
  const wanted = normalizeName(query);
  if (wanted.length < NEAR_MIN) return [];
  const typed = new Set(wanted.split(" "));
  return items.filter((role) => {
    const name = normalizeName(role.name);
    return (
      name.includes(wanted) ||
      wanted.includes(name) ||
      acronymOf(name) === wanted ||
      name.split(" ").some((word) => typed.has(word))
    );
  });
}

const DESCRIPTION_MAX = 200;
const NAME_MAX = 200;

/* The catalog's own rules, shared by the client's form and the server's
   route — the server is the authority (AA2), the client just says the same
   thing sooner. */
export function validateRoleInput(
  input: { name: unknown; description?: unknown; cardinality: unknown },
  roles: Role[],
): string | null {
  const { name, description, cardinality } = input;
  if (typeof name !== "string" || !name.trim()) return "a role name is required";
  if (name.trim().length > NAME_MAX) return `the role name is at most ${NAME_MAX} characters`;
  /* An exact name is refused; a near match never is (CR4). The comparison is
     by SLUG, not by name, because the slug is the identity AND the storage
     key: "Project-Lead" and "Project Lead" are one role however they are
     spelled, and letting the second through would overwrite the first — its
     single-holder flag and its stamp included (review finding 1). */
  const wanted = slugId(name);
  const taken = roles.find((r) => r.id === wanted || slugId(r.name) === wanted);
  if (taken) return `a role named "${taken.name}" already exists`;
  if (cardinality !== "one" && cardinality !== "many")
    return "say whether one person or several may fill the role";
  if (description !== undefined && description !== null && description !== "") {
    /* a type error is not a length error — say which it is (review 13) */
    if (typeof description !== "string") return "the description must be text";
    /* every line break, not just \n (review 11) */
    if (/[\r\n\u2028\u2029]/.test(description) || description.trim().length > DESCRIPTION_MAX)
      return `the description is one line of at most ${DESCRIPTION_MAX} characters`;
  }
  return null;
}

/* What an edit may change (CR12): the name, the description, how many may
   hold it, and whether it is in circulation — never the id. */
export interface RoleEdit {
  name?: string;
  description?: string | null;
  cardinality?: Role["cardinality"];
  retired?: boolean;
}

/* The rules for changing a role already in the catalog. Same rules as
   adding one, with two differences that matter (CR14): the role must exist,
   and it cannot collide with ITSELF — re-saving after a capitalisation fix
   is not a duplicate. */
export function validateRoleEdit(edit: RoleEdit, roles: Role[], id: string): string | null {
  const current = roles.find((r) => r.id === id);
  if (!current) return "that role is not in the catalog";
  const next = { ...current, ...edit };
  /* every role BUT this one is what a rename may not collide with */
  return validateRoleInput(next, roles.filter((r) => r.id !== id));
}

/* Apply an edit as the next revision (CR13). The id comes from the role
   being edited and is NEVER re-derived from the name — that is the whole
   of CR12, and re-slugging here would orphan every allocation and role need
   recorded against this role. */
export function applyRoleEdit(current: Role, edit: RoleEdit, by: UserRef, date: string): Role {
  const named = edit.name === undefined ? current.name : edit.name.trim().replace(/\s+/g, " ");
  return {
    ...current,
    id: current.id,
    name: named,
    cardinality: edit.cardinality ?? current.cardinality,
    description:
      edit.description === undefined
        ? current.description
        : typeof edit.description === "string" && edit.description.trim()
          ? edit.description.trim()
          : null,
    retired: edit.retired ?? current.retired ?? false,
    recorded: { source: ENTERED_SRC, date, by },
  };
}

/* Which projects a tightening to one holder would contradict (CR15) — the
   answer is a warning, never a refusal. */
export function tighteningConflicts(
  roleId: string,
  cardinality: Role["cardinality"],
  holders: { projectId: string; roleId: string }[],
): string[] {
  if (cardinality !== "one") return [];
  const perProject = new Map<string, number>();
  for (const h of holders)
    if (h.roleId === roleId) perProject.set(h.projectId, (perProject.get(h.projectId) ?? 0) + 1);
  return [...perProject.entries()].filter(([, n]) => n > 1).map(([projectId]) => projectId);
}

/* an absent or blank description is NOT RECORDED, never an empty value */
export const cleanRoleInput = (input: {
  name: string;
  description?: unknown;
  cardinality: Role["cardinality"];
}): RoleInput => ({
  name: input.name.trim().replace(/\s+/g, " "),
  description: typeof input.description === "string" && input.description.trim()
    ? input.description.trim()
    : null,
  cardinality: input.cardinality,
});

/* The migration (CR5): the twelve roles the app hardcoded until Aug 24 2026,
   ids and single-holder flags unchanged so every recorded allocation and role
   need keeps resolving. The migration is the source, its date is the date —
   and there is NO recorder, because no person recorded them. */
export const MIGRATION_SRC = "Migrated from the app's built-in role list · Aug 24, 2026";
export const MIGRATION_DATE = "2026-08-24T00:00:00.000Z";

const migrated = (name: string, cardinality: Role["cardinality"]): Role => ({
  id: slugId(name),
  name,
  cardinality,
  description: null,
  recorded: { source: MIGRATION_SRC, date: MIGRATION_DATE },
});

export const MIGRATED_ROLES: Role[] = [
  migrated("Project Lead", "one"),
  migrated("Project Manager", "one"),
  migrated("Scrum Master", "one"),
  migrated("Embedded Engineer", "many"),
  migrated("Perception Engineer", "many"),
  migrated("GNC Engineer", "many"),
  migrated("Planning Engineer", "many"),
  migrated("Systems Engineer", "many"),
  migrated("Software Developer in Test", "many"),
  migrated("DevOps Engineer", "many"),
  migrated("Verification Engineer", "many"),
  migrated("Functional Safety Engineer", "many"),
];
